/**
 * Email Header Parser — RFC 5322 header analysis service
 *
 * Parses raw email headers into structured data: summary fields,
 * Received chain, authentication results, anti-spam headers,
 * threat indicators, and IP extractions.
 *
 * Pure functions only — no side effects, no class instances.
 * Modeled after Microsoft Message Header Analyzer (MHA) approach.
 *
 * @module services/email-header-parser
 */

// ── Constants ──

const HEADER_REGEX = /^([\w-.]*?):\s?(.*)/;
const DIGIT_PREFIX = /^\d{1,2}$/;
const IPV4_REGEX = /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/;
const IPV6_REGEX = /\b([a-fA-F0-9:]+(?::[a-fA-F0-9:]+)+)\b/;

const SEVERITY_MAP = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  info: 'info',
};

const AUTH_METHODS = ['spf', 'dkim', 'dmarc', 'arc'];

const SUMMARY_HEADERS = [
  'from', 'to', 'subject', 'date', 'message-id',
  'return-path', 'reply-to', 'mime-version', 'content-type',
  'cc', 'bcc', 'sender',
];

const ANTISPAM_HEADER_PREFIXES = [
  'x-forefront-antispam-report',
  'x-ms-exchange-',
  'x-proofpoint-',
  'x-spam-',
];

// ── Utility Helpers ──

/**
 * Normalize line endings to \n for consistent processing.
 * @param {string} text
 * @returns {string}
 */
function normalizeLineEndings(text) {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/**
 * Check if a line is a continuation of the previous header (folding).
 * Folding lines begin with whitespace (space or tab).
 * @param {string} line
 * @returns {boolean}
 */
function isFoldingLine(line) {
  return line.length > 0 && (line[0] === ' ' || line[0] === '\t');
}

/**
 * Collapse folding whitespace to a single space.
 * @param {string} value
 * @returns {string}
 */
function collapseWhitespace(value) {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Extract all IPv4 and IPv6 addresses from a string.
 * @param {string} text
 * @returns {string[]}
 */
function extractIPs(text) {
  if (!text) return [];
  const ips = new Set();
  let match;

  const ipv4Re = new RegExp(IPV4_REGEX.source, 'g');
  while ((match = ipv4Re.exec(text)) !== null) {
    const ip = match[1];
    // Validate IPv4 octets
    const octets = ip.split('.').map(Number);
    if (octets.every(o => o >= 0 && o <= 255)) {
      ips.add(ip);
    }
  }

  const ipv6Re = new RegExp(IPV6_REGEX.source, 'g');
  while ((match = ipv6Re.exec(text)) !== null) {
    ips.add(match[1].toLowerCase());
  }

  return [...ips];
}

/**
 * Normalize a header name to lowercase for case-insensitive matching.
 * @param {string} name
 * @returns {string}
 */
function normalizeName(name) {
  return name.trim().toLowerCase();
}

// ── 1. getHeaderList ──

/**
 * Parse raw RFC 5322 header text into an array of { name, value } objects.
 *
 * Handles:
 * - Mixed line endings (\r\n, \r, \n)
 * - Header folding (continuation lines starting with whitespace)
 * - Body boundary detection (empty line separates headers from body)
 * - Edge case: lines starting with digits+colon (e.g., "16:20:05") are NOT headers
 *
 * @param {string} rawText - Raw email header text
 * @returns {Array<{name: string, value: string}>} Parsed header list
 */
export function getHeaderList(rawText) {
  if (!rawText) return [];

  const text = normalizeLineEndings(rawText);
  const lines = text.split('\n');
  const headers = [];
  let bodyStarted = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Body boundary: empty line after headers section
    if (bodyStarted) break;
    if (line === '') {
      if (headers.length > 0) bodyStarted = true;
      continue;
    }

    // Check for header continuation (folding)
    if (isFoldingLine(line) && headers.length > 0) {
      const last = headers[headers.length - 1];
      const folded = line.replace(/^[\s]+/, '');
      if (folded) {
        last.value = last.value ? last.value + ' ' + folded : folded;
      }
      continue;
    }

    // Try to match as a new header
    const match = line.match(HEADER_REGEX);

    // Reject false positives: lines like "16:20:05 -0400" that match
    // the header pattern but are just time values on folded lines
    if (match && match[1] && !DIGIT_PREFIX.test(match[1])) {
      headers.push({
        name: match[1].trim(),
        value: (match[2] || '').trim(),
      });
    } else if (headers.length === 0) {
      // Leading non-header lines before any header — ignore preamble
      continue;
    } else {
      // Unmatched line after headers — tack to previous as folding
      const last = headers[headers.length - 1];
      const folded = line.replace(/^[\s]+/, '');
      if (folded) {
        last.value = last.value ? last.value + ' ' + folded : folded;
      }
    }
  }

  return headers;
}

// ── 2. parseSummaryFields ──

/**
 * Extract common summary fields from a parsed header list.
 *
 * Returns an object with commonly used email headers:
 * from, to, subject, date, messageId, returnPath, replyTo,
 * mimeVersion, contentType, cc, sender.
 *
 * Multiple To/Cc fields are joined with "; ".
 *
 * @param {Array<{name: string, value: string}>} headerList
 * @returns {object} Summary fields
 */
export function parseSummaryFields(headerList) {
  if (!headerList || headerList.length === 0) {
    return {
      from: '', to: '', subject: '', date: '', messageId: '',
      returnPath: '', replyTo: '', mimeVersion: '', contentType: '',
      cc: '', sender: '',
    };
  }

  const summary = {};

  for (const header of headerList) {
    const name = normalizeName(header.name);
    const value = collapseWhitespace(header.value);
    if (!value) continue;

    switch (name) {
      case 'from':
        summary.from = summary.from ? summary.from : value;
        break;
      case 'to':
        summary.to = summary.to ? summary.to + '; ' + value : value;
        break;
      case 'cc':
        summary.cc = summary.cc ? summary.cc + '; ' + value : value;
        break;
      case 'subject':
        summary.subject = summary.subject ? summary.subject : value;
        break;
      case 'date':
        summary.date = summary.date ? summary.date : value;
        break;
      case 'message-id':
      case 'message-id':
        summary.messageId = summary.messageId ? summary.messageId : value;
        break;
      case 'return-path':
        summary.returnPath = summary.returnPath ? summary.returnPath : value;
        break;
      case 'reply-to':
        summary.replyTo = summary.replyTo ? summary.replyTo : value;
        break;
      case 'mime-version':
        summary.mimeVersion = summary.mimeVersion ? summary.mimeVersion : value;
        break;
      case 'content-type':
        summary.contentType = summary.contentType ? summary.contentType : value;
        break;
      case 'sender':
        summary.sender = summary.sender ? summary.sender : value;
        break;
      default:
        break;
    }
  }

  return {
    from: summary.from || '',
    to: summary.to || '',
    subject: summary.subject || '',
    date: summary.date || '',
    messageId: summary.messageId || '',
    returnPath: summary.returnPath || '',
    replyTo: summary.replyTo || '',
    mimeVersion: summary.mimeVersion || '',
    contentType: summary.contentType || '',
    cc: summary.cc || '',
    sender: summary.sender || '',
  };
}

// ── 3. parseReceivedChain ──

/**
 * Parse the Received header chain from a parsed header list.
 *
 * Each Received header is parsed for:
 * - from (originating hostname/IP)
 * - by (receiving hostname)
 * - with (protocol: ESMTP, SMTP, etc.)
 * - via (transport type)
 * - id (message transit ID)
 * - for (envelope recipient)
 * - timestamp (date-time string)
 *
 * Returns array in chronological order (first = origin, last = destination).
 *
 * @param {Array<{name: string, value: string}>} headerList
 * @returns {Array<{from: string, by: string, with: string, via: string, id: string, for: string, timestamp: string}>}
 */
export function parseReceivedChain(headerList) {
  if (!headerList || headerList.length === 0) return [];

  const receivedHeaders = [];

  for (const header of headerList) {
    if (normalizeName(header.name) !== 'received') continue;
    receivedHeaders.push(header.value);
  }

  // Parse each Received header value
  const chain = receivedHeaders.map((value, index) => {
    return parseSingleReceived(value, index);
  });

  // Received headers are in reverse chronological order (newest first in email).
  // Reverse to get chronological order (first hop = origin).
  return chain.reverse();
}

/**
 * Parse a single Received header value into structured fields.
 * @param {string} value - Raw Received header value
 * @param {number} index - Position in the original list
 * @returns {{from: string, by: string, with: string, via: string, id: string, for: string, timestamp: string, raw: string}}
 */
function parseSingleReceived(value, index) {
  if (!value) {
    return { from: '', by: '', with: '', via: '', id: '', for: '', timestamp: '', raw: '', hop: index + 1 };
  }

  // Clean up the value for parsing
  let cleaned = value.replace(/\r|\n/g, ' ').trim();

  // Extract date/timestamp — always at the end after the last ";"
  let timestamp = '';
  const lastSemi = cleaned.lastIndexOf(';');
  if (lastSemi !== -1 && lastSemi < cleaned.length - 1) {
    timestamp = cleaned.substring(lastSemi + 1).trim();
    cleaned = cleaned.substring(0, lastSemi).trim();
  }

  // If no semicolon found, try to find date by day-of-week pattern
  if (!timestamp) {
    const dayMatch = cleaned.match(/\s*(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s/);
    if (dayMatch) {
      const idx = dayMatch.index;
      timestamp = cleaned.substring(idx).trim();
      cleaned = cleaned.substring(0, idx).trim();
    }
  }

  // Parse structured fields from the remaining text
  // Pattern: from [...] by [...] via [...] with [...] id [...] for [...]
  const fields = {
    from: extractReceivedField(cleaned, 'from'),
    by: extractReceivedField(cleaned, 'by'),
    with: extractReceivedField(cleaned, 'with'),
    via: extractReceivedField(cleaned, 'via'),
    id: extractReceivedField(cleaned, 'id'),
    for: extractReceivedField(cleaned, 'for'),
  };

  return {
    from: fields.from || '',
    by: fields.by || '',
    with: fields.with || '',
    via: fields.via || '',
    id: fields.id || '',
    for: fields.for || '',
    timestamp: timestamp || '',
    raw: value,
    hop: index + 1,
  };
}

/**
 * Extract a specific keyword-led field from a Received header value.
 * e.g., extractReceivedField("from mail.example.com by mx.example.org", "from")
 *   → "mail.example.com"
 *
 * @param {string} text - Cleaned Received header value
 * @param {string} fieldName - Field keyword (from, by, with, etc.)
 * @returns {string} Extracted field value or empty string
 */
function extractReceivedField(text, fieldName) {
  // Build regex: keyword followed by content until next keyword or end
  // We use a word boundary to avoid partial matches
  const pattern = new RegExp(
    `\\b${fieldName}\\s+((?:(?!\\b(?:from|by|with|via|id|for)\\b)[^;])+)`,
    'i'
  );
  const match = text.match(pattern);
  if (!match) return '';

  return match[1].trim().replace(/\s+/g, ' ');
}

// ── 4. extractIPsFromReceived ──

/**
 * Extract all unique IP addresses from the Received chain.
 * Parses IPv4 and IPv6 addresses from both "from" and "by" clauses.
 *
 * @param {Array<{from: string, by: string, ...}>} receivedChain
 * @returns {string[]} Unique IP strings
 */
export function extractIPsFromReceived(receivedChain) {
  if (!receivedChain || receivedChain.length === 0) return [];

  const ips = new Set();

  for (const hop of receivedChain) {
    if (hop.from) {
      const fromIPs = extractIPs(hop.from);
      fromIPs.forEach(ip => ips.add(ip));
    }
    if (hop.by) {
      const byIPs = extractIPs(hop.by);
      byIPs.forEach(ip => ips.add(ip));
    }
  }

  return [...ips];
}

// ── 5. parseAuthResults ──

/**
 * Parse Authentication-Results headers for SPF, DKIM, and DMARC results.
 *
 * Handles multiple auth-results headers and RFC 8601 syntax:
 *   Authentication-Results: mx.example.com; spf=pass smtp.mailfrom=user@example.com
 *
 * @param {Array<{name: string, value: string}>} headerList
 * @returns {{ spf: object|null, dkim: object|null, dmarc: object|null, arc: object|null }}
 */
export function parseAuthResults(headerList) {
  if (!headerList || headerList.length === 0) {
    return { spf: null, dkim: null, dmarc: null, arc: null };
  }

  const results = { spf: null, dkim: null, dmarc: null, arc: null };

  for (const header of headerList) {
    if (normalizeName(header.name) !== 'authentication-results') continue;

    const parsed = parseSingleAuthResult(header.value);
    if (!parsed) continue;

    // Merge parsed results — later headers may override
    for (const method of AUTH_METHODS) {
      if (parsed[method]) {
        // If we already have a result, only override if the new one is more specific
        if (!results[method] || !results[method].result) {
          results[method] = parsed[method];
        }
      }
    }
  }

  return {
    spf: results.spf,
    dkim: results.dkim,
    dmarc: results.dmarc,
    arc: results.arc,
  };
}

/**
 * Parse a single Authentication-Results header value.
 * @param {string} value
 * @returns {object|null} Parsed methods or null
 */
function parseSingleAuthResult(value) {
  if (!value) return null;

  const parsed = {};

  // Extract SPF result
  const spfMatch = value.match(
    /spf=(\w+)\s*(?:smtp\.mailfrom|mailfrom|header\.from|helo)=([^\s;]+)/i
  );
  if (spfMatch) {
    parsed.spf = {
      result: spfMatch[1].toLowerCase(),
      domain: spfMatch[2].replace(/[<>]/g, ''),
      scope: spfMatch[0].includes('smtp.mailfrom') ? 'mfrom' : 'helo',
    };
  } else {
    // Simpler spf match
    const spfSimple = value.match(/spf=(\w+)/i);
    if (spfSimple) {
      const domainMatch = value.match(/smtp\.mailfrom=([^\s;]+)/i);
      parsed.spf = {
        result: spfSimple[1].toLowerCase(),
        domain: domainMatch ? domainMatch[1].replace(/[<>]/g, '') : '',
        scope: 'unknown',
      };
    }
  }

  // Extract DKIM result
  const dkimMatch = value.match(
    /dkim=(\w+)\s*(?:header\.d|d|domain)=([^\s;]+)\s*(?:header\.s|s|selector)=([^\s;]+)/i
  );
  if (dkimMatch) {
    parsed.dkim = {
      result: dkimMatch[1].toLowerCase(),
      domain: dkimMatch[2].replace(/[<>]/g, ''),
      selector: dkimMatch[3].replace(/[<>]/g, ''),
    };
  } else {
    const dkimSimple = value.match(/dkim=(\w+)/i);
    if (dkimSimple) {
      const dDomain = value.match(/(?:header\.d|d|domain)=([^\s;]+)/i);
      const dSelector = value.match(/(?:header\.s|s|selector)=([^\s;]+)/i);
      parsed.dkim = {
        result: dkimSimple[1].toLowerCase(),
        domain: dDomain ? dDomain[1].replace(/[<>]/g, '') : '',
        selector: dSelector ? dSelector[1].replace(/[<>]/g, '') : '',
      };
    }
  }

  // Extract DMARC result
  const dmarcMatch = value.match(
    /dmarc=(\w+)\s*(?:header\.from|from)=([^\s;]+)/i
  );
  if (dmarcMatch) {
    parsed.dmarc = {
      result: dmarcMatch[1].toLowerCase(),
      domain: dmarcMatch[2].replace(/[<>]/g, ''),
    };
  } else {
    const dmarcSimple = value.match(/dmarc=(\w+)/i);
    if (dmarcSimple) {
      const dDomain = value.match(/(?:header\.from|from)=([^\s;]+)/i);
      parsed.dmarc = {
        result: dmarcSimple[1].toLowerCase(),
        domain: dDomain ? dDomain[1].replace(/[<>]/g, '') : '',
      };
    }
  }

  // Extract ARC result
  const arcMatch = value.match(/arc=(\w+)/i);
  if (arcMatch) {
    parsed.arc = { result: arcMatch[1].toLowerCase(), domain: '' };
  }

  return Object.keys(parsed).length > 0 ? parsed : null;
}

// ── 6. parseAntiSpamHeaders ──

/**
 * Parse anti-spam headers into categorized results.
 *
 * Recognized header families:
 * - X-Forefront-Antispam-Report (Exchange Online Protection)
 * - X-MS-Exchange-* (Microsoft Exchange)
 * - X-Proofpoint-* (Proofpoint)
 * - X-Spam-* (SpamAssassin style)
 *
 * @param {Array<{name: string, value: string}>} headerList
 * @returns {object} Categorized anti-spam results
 */
export function parseAntiSpamHeaders(headerList) {
  if (!headerList || headerList.length === 0) {
    return {
      forefront: {},
      exchange: {},
      proofpoint: {},
      spam: {},
      other: {},
    };
  }

  const result = {
    forefront: {},
    exchange: {},
    proofpoint: {},
    spam: {},
    other: {},
  };

  for (const header of headerList) {
    const name = normalizeName(header.name);
    const value = collapseWhitespace(header.value);
    if (!value) continue;

    if (name === 'x-forefront-antispam-report') {
      result.forefront = parseForefrontReport(value);
    } else if (name.startsWith('x-ms-exchange-')) {
      result.exchange[name] = value;
    } else if (name.startsWith('x-proofpoint-')) {
      result.proofpoint[name] = value;
    } else if (name.startsWith('x-spam-')) {
      result.spam[name] = value;
    }
  }

  return result;
}

/**
 * Parse X-Forefront-Antispam-Report value into key-value pairs.
 * Format: SFV:SKI;CAT:SPOOF;SFS:...
 * @param {string} report
 * @returns {object}
 */
function parseForefrontReport(report) {
  if (!report) return {};

  // Clean null placeholders and normalize separators
  let cleaned = report.replace(/\(null\)/g, '').replace(/;+/g, ';');
  if (!cleaned.endsWith(';')) cleaned += ';';

  const fields = {};
  const pairs = cleaned.match(/([\w-]+):([^;]*);/g);

  if (pairs) {
    for (const pair of pairs) {
      const match = pair.match(/([\w-]+):([^;]*);/);
      if (match && match[1]) {
        fields[match[1]] = (match[2] || '').trim();
      }
    }
  }

  return fields;
}

// ── 7. identifyThreatIndicators ──

/**
 * Analyze parsed headers for security threat indicators.
 *
 * Checks performed:
 * - SPF/DKIM/DMARC failures → threat indicators
 * - Known-bad IPs in Received chain (via enrichments param)
 * - From domain vs Return-Path domain mismatch → spoofing
 * - ARC seal failures → threat indicator
 * - Missing authentication results → informational
 *
 * @param {object} summary - Result from parseSummaryFields
 * @param {Array} receivedChain - Result from parseReceivedChain
 * @param {object} authResults - Result from parseAuthResults
 * @param {object} antiSpam - Result from parseAntiSpamHeaders
 * @param {object} enrichments - Map of ip → enrichment data (with threatIntel)
 * @returns {Array<{type: string, severity: string, title: string, description: string, source: string}>}
 */
export function identifyThreatIndicators(summary, receivedChain, authResults, antiSpam, enrichments) {
  const indicators = [];

  // ── SPF Failures ──
  if (authResults && authResults.spf) {
    const failResults = ['fail', 'softfail', 'permerror', 'temperror'];
    if (failResults.includes(authResults.spf.result)) {
      indicators.push({
        type: 'spf_fail',
        severity: authResults.spf.result === 'fail' ? 'high' : 'medium',
        title: 'SPF Authentication Failed',
        description: `SPF check returned "${authResults.spf.result}" for domain "${authResults.spf.domain}"`,
        source: 'email-header-parser',
      });
    }
  }

  // ── DKIM Failures ──
  if (authResults && authResults.dkim) {
    const failResults = ['fail', 'neutral', 'permerror', 'temperror'];
    if (failResults.includes(authResults.dkim.result)) {
      indicators.push({
        type: 'dkim_fail',
        severity: authResults.dkim.result === 'fail' ? 'high' : 'medium',
        title: 'DKIM Signature Verification Failed',
        description: `DKIM check returned "${authResults.dkim.result}" for domain "${authResults.dkim.domain}"`,
        source: 'email-header-parser',
      });
    }
  }

  // ── DMARC Failures ──
  if (authResults && authResults.dmarc) {
    const failResults = ['fail', 'reject', 'permerror', 'temperror'];
    if (failResults.includes(authResults.dmarc.result)) {
      indicators.push({
        type: 'dmarc_fail',
        severity: 'critical',
        title: 'DMARC Validation Failed',
        description: `DMARC check returned "${authResults.dmarc.result}" for domain "${authResults.dmarc.domain}"`,
        source: 'email-header-parser',
      });
    }
  }

  // ── ARC Seal Failures ──
  if (authResults && authResults.arc) {
    const failResults = ['fail', 'none'];
    if (failResults.includes(authResults.arc.result)) {
      indicators.push({
        type: 'arc_fail',
        severity: 'high',
        title: 'ARC Seal Authentication Failed',
        description: `ARC (Authenticated Received Chain) returned "${authResults.arc.result}"`,
        source: 'email-header-parser',
      });
    }
  }

  // ── From/Return-Path Domain Mismatch ──
  if (summary && summary.from && summary.returnPath) {
    const fromDomain = extractDomain(summary.from);
    const returnPathDomain = extractDomain(summary.returnPath);

    if (fromDomain && returnPathDomain && fromDomain !== returnPathDomain) {
      indicators.push({
        type: 'domain_mismatch',
        severity: 'high',
        title: 'From Domain / Return-Path Domain Mismatch',
        description: `Email claims to be from "${fromDomain}" but return path points to "${returnPathDomain}"`,
        source: 'email-header-parser',
      });
    }
  }

  // ── Known-Bad IPs in Received Chain ──
  if (receivedChain && receivedChain.length > 0 && enrichments) {
    const seenIPs = new Set();

    for (const hop of receivedChain) {
      const ips = extractIPsFromReceived([hop]);
      for (const ip of ips) {
        if (seenIPs.has(ip)) continue;
        seenIPs.add(ip);

        const enrichment = enrichments[ip];
        if (!enrichment || !enrichment.threatIntel) continue;

        const threatIntel = enrichment.threatIntel;

        // Check AbuseIPDB
        if (threatIntel.abuseIPDB && threatIntel.abuseIPDB.abuseConfidenceScore >= 50) {
          indicators.push({
            type: 'known_bad_ip',
            severity: 'critical',
            title: 'Known Malicious IP in Received Chain',
            description: `IP ${ip} has AbuseIPDB confidence score of ${threatIntel.abuseIPDB.abuseConfidenceScore} (hop ${hop.hop})`,
            source: 'AbuseIPDB',
          });
        }

        // Check GreyNoise
        if (threatIntel.greynoise) {
          const gn = threatIntel.greynoise;
          const isMalicious = gn.enterprise?.classification === 'malicious' ||
            gn.community?.classification === 'malicious';
          if (isMalicious) {
            indicators.push({
              type: 'known_bad_ip',
              severity: 'critical',
              title: 'Known Malicious IP (GreyNoise) in Received Chain',
              description: `IP ${ip} classified as malicious by GreyNoise (hop ${hop.hop})`,
              source: 'GreyNoise',
            });
          }
        }

        // Check AlienVault
        if (threatIntel.alienVault && threatIntel.alienVault.pulseCount > 0) {
          indicators.push({
            type: 'known_bad_ip',
            severity: 'high',
            title: 'IP Reported in Threat Intelligence Feed',
            description: `IP ${ip} appears in ${threatIntel.alienVault.pulseCount} AlienVault OTX pulses (hop ${hop.hop})`,
            source: 'AlienVault OTX',
          });
        }
      }
    }
  }

  // ── Missing Authentication Results ──
  if (!authResults || (!authResults.spf && !authResults.dkim && !authResults.dmarc)) {
    indicators.push({
      type: 'missing_auth',
      severity: 'info',
      title: 'No Email Authentication Results',
      description: 'No SPF, DKIM, or DMARC authentication results found in headers. Email may lack authentication.',
      source: 'email-header-parser',
    });
  } else {
    // Check for individual missing methods
    const missingMethods = [];
    if (!authResults.spf) missingMethods.push('SPF');
    if (!authResults.dkim) missingMethods.push('DKIM');
    if (!authResults.dmarc) missingMethods.push('DMARC');

    if (missingMethods.length > 0 && missingMethods.length < 3) {
      indicators.push({
        type: 'missing_auth_method',
        severity: 'low',
        title: 'Missing Email Authentication Method',
        description: `Email is missing ${missingMethods.join(' and ')} authentication results`,
        source: 'email-header-parser',
      });
    }
  }

  // ── Exchange Anti-Spam High Confidence Indicators ──
  if (antiSpam && antiSpam.forefront) {
    const sfv = antiSpam.forefront.SFV;
    if (sfv) {
      const spamResults = {
        SPM: { severity: 'high', title: 'Marked as Spam', desc: 'X-Forefront-Antispam-Report indicates spam (SFV:SPM)' },
        SPOOF: { severity: 'high', title: 'Spoof Detection', desc: 'X-Forefront-Antispam-Report indicates spoof (SFV:SPOOF)' },
        SKI: { severity: 'medium', title: 'Skipped Filtering', desc: 'X-Forefront-Antispam-Report indicates skipped filtering (SFV:SKI)' },
        GRA: { severity: 'info', title: 'Graph-Only Filtering', desc: 'X-Forefront-Antispam-Report indicates graph-only (SFV:GRA)' },
        BULK: { severity: 'low', title: 'Bulk Email Detected', desc: 'X-Forefront-Antispam-Report indicates bulk email (SFV:BULK)' },
      };

      const sfvUpper = sfv.toUpperCase();
      if (spamResults[sfvUpper]) {
        indicators.push({
          type: 'antispam_' + sfvUpper.toLowerCase(),
          severity: spamResults[sfvUpper].severity,
          title: spamResults[sfvUpper].title,
          description: spamResults[sfvUpper].desc,
          source: 'X-Forefront-Antispam-Report',
        });
      }
    }

    // Check CAT field for specific categories
    const cat = antiSpam.forefront.CAT;
    if (cat) {
      const catUpper = cat.toUpperCase();
      if (catUpper.includes('SPOOF') || catUpper.includes('PHISH')) {
        indicators.push({
          type: 'antispam_cat_spoof',
          severity: 'critical',
          title: 'Anti-Spam Category: Spoof/Phish',
          description: `X-Forefront-Antispam-Report categorizes as "${cat}"`,
          source: 'X-Forefront-Antispam-Report',
        });
      }
    }
  }

  return indicators;
}

/**
 * Extract domain from an email address string.
 * Handles formats: "user@domain.com", "<user@domain.com>", "Display Name <user@domain.com>"
 * @param {string} email
 * @returns {string|null}
 */
function extractDomain(email) {
  if (!email) return null;

  // Extract email address from angle brackets
  const angleMatch = email.match(/<([^>]+)>/);
  const addr = angleMatch ? angleMatch[1] : email.trim();

  // Extract domain after @
  const atIndex = addr.lastIndexOf('@');
  if (atIndex === -1) return null;

  return addr.substring(atIndex + 1).toLowerCase().trim();
}

// ── 8. parseEmailHeaders (main entry point) ──

/**
 * Main entry point: parse raw email headers into a complete structured result.
 *
 * @param {string} rawText - Raw RFC 5322 email header text
 * @returns {{ summary: object, receivedChain: Array, ips: string[], authResults: object, antiSpam: object, rawHeaders: Array }}
 */
export function parseEmailHeaders(rawText) {
  if (!rawText || typeof rawText !== 'string') {
    return {
      summary: null,
      receivedChain: [],
      ips: [],
      authResults: { spf: null, dkim: null, dmarc: null, arc: null },
      antiSpam: { forefront: {}, exchange: {}, proofpoint: {}, spam: {}, other: {} },
      rawHeaders: [],
    };
  }

  try {
    const rawHeaders = getHeaderList(rawText);
    const summary = parseSummaryFields(rawHeaders);
    const receivedChain = parseReceivedChain(rawHeaders);
    const ips = extractIPsFromReceived(receivedChain);
    const authResults = parseAuthResults(rawHeaders);
    const antiSpam = parseAntiSpamHeaders(rawHeaders);

    return {
      summary,
      receivedChain,
      ips,
      authResults,
      antiSpam,
      rawHeaders,
    };
  } catch (err) {
    console.warn('[email-header-parser] parseEmailHeaders failed:', err.message);
    return {
      summary: null,
      receivedChain: [],
      ips: [],
      authResults: { spf: null, dkim: null, dmarc: null, arc: null },
      antiSpam: { forefront: {}, exchange: {}, proofpoint: {}, spam: {}, other: {} },
      rawHeaders: [],
      error: err.message,
    };
  }
}

// ── 9. analyzeHeadersWithIntel ──

/**
 * Parse email headers and analyze them with threat intelligence enrichment data.
 *
 * Combines parsing with threat detection by passing enrichment data
 * to identifyThreatIndicators.
 *
 * @param {{ summary: object, receivedChain: Array, ips: string[], authResults: object, antiSpam: object, rawHeaders: Array }} parsedResult
 * @param {object} enrichments - Map of ip → enrichment data (with threatIntel)
 * @returns {{ summary: object, receivedChain: Array, ips: string[], authResults: object, antiSpam: object, rawHeaders: Array, indicators: Array }}
 */
export function analyzeHeadersWithIntel(parsedResult, enrichments) {
  if (!parsedResult) {
    return {
      summary: null,
      receivedChain: [],
      ips: [],
      authResults: { spf: null, dkim: null, dmarc: null, arc: null },
      antiSpam: { forefront: {}, exchange: {}, proofpoint: {}, spam: {}, other: {} },
      rawHeaders: [],
      indicators: [],
    };
  }

  try {
    const indicators = identifyThreatIndicators(
      parsedResult.summary,
      parsedResult.receivedChain,
      parsedResult.authResults,
      parsedResult.antiSpam,
      enrichments || {}
    );

    return {
      ...parsedResult,
      indicators,
    };
  } catch (err) {
    console.warn('[email-header-parser] analyzeHeadersWithIntel failed:', err.message);
    return {
      ...parsedResult,
      indicators: [],
      error: err.message,
    };
  }
}
